<template>
  <div
    :class="[
      'rounded-full bg-cover bg-center flex items-center justify-center font-bold text-white flex-shrink-0',
      !url && (fallbackClass ?? 'bg-gray-300 dark:bg-gray-600'),
      containerClass,
    ]"
    :style="bgStyle"
    :aria-label="initials || undefined">
    <span v-if="!url" class="select-none">{{ initials }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { fetchAvatarUrl } from '../composables/useUserAvatar';

/**
 * Reusable user avatar that works in every connection mode (local browser,
 * cloud WebRTC, Electron). The avatar is fetched as a blob through the routed
 * API layer (see useUserAvatar.ts) instead of a raw relative URL, which cannot
 * cross the WebRTC tunnel / Electron IPC bridge.
 *
 * Pass `userId` (normally only when the person has an avatar) and `initials`;
 * while no avatar is available (or the person has none) the initials are shown.
 * `containerClass` carries size / text-size / dark-mode tweaks, e.g.
 * `w-9 h-9 text-sm` or `w-7 h-7 dark:bg-gray-500 text-xs`. `fallbackClass`
 * replaces the grey initials placeholder (e.g. the map's green disc).
 */
const props = defineProps<{
  userId?: string | null;
  initials?: string;
  containerClass?: string;
  fallbackClass?: string;
}>();

const url = ref<string | null>(null);

const bgStyle = computed(() => (url.value ? { backgroundImage: `url('${url.value}')` } : {}));

watch(
  () => props.userId,
  async (id) => {
    if (!id) {
      url.value = null;
      return;
    }
    url.value = await fetchAvatarUrl(id);
  },
  { immediate: true },
);
</script>
